import React, { useState, useRef } from 'react';
import { 
  Palette, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, 
  Upload, Image, XCircle, Maximize2, X, FileText, 
  ExternalLink, Loader2, Eye, Download, Check, Save,
  Camera, Plus, Trash2, Star, Ban
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { ASSET_CONFIGS, getActualValue } from './assetConfigs';

const ArtDetailModal = React.memo(({ 
  item, 
  items, 
  currentIndex, 
  onClose, 
  onSave, 
  onNavigate,
  formatCurrency,
  API_URL,
  userEmail,
  isNewItem = false
}) => {
  const getActualValueLocal = (i) => {
    if (i.currentPrice && i.currentPrice > 0) return i.currentPrice;
    if (i.shares && i.shares > 1) return i.shares;
    return i.currentPrice || 0;
  };
  
  const actualValue = getActualValueLocal(item);
  const actualCost = item.costBasis || 0;
  
  const [editForm, setEditForm] = useState({ 
    ...item,
    shares: actualValue,
    // Map old field names to new ones
    artArtist: item.artArtist || item.artist || ''
  });
  const [photos, setPhotos] = useState(item.photos || []);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState(item.documents || []);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [saving, setSaving] = useState(false);
  const docInputRef = useRef(null);
  const [viewingDocument, setViewingDocument] = useState(null);
  
  // Section collapse states - cached to avoid localStorage reads on every render
  const initialCollapseState = useRef(null);
  if (initialCollapseState.current === null) {
    try {
      const saved = localStorage.getItem('artModalCollapseState');
      initialCollapseState.current = saved ? JSON.parse(saved) : { details: true, photos: true, documents: false };
    } catch (e) {
      initialCollapseState.current = { details: true, photos: true, documents: false };
    }
  }
  
  const [collapseState, setCollapseState] = useState(initialCollapseState.current);
  
  const updateCollapseState = (key, value) => {
    setCollapseState(prev => {
      const newState = { ...prev, [key]: value };
      // Debounce localStorage write
      setTimeout(() => {
        try { localStorage.setItem('artModalCollapseState', JSON.stringify(newState)); } catch { /* ignore */ }
      }, 100);
      return newState;
    });
  };
  
  const detailsExpanded = collapseState.details;
  const photosExpanded = collapseState.photos;
  const documentsExpanded = collapseState.documents;
  
  const setDetailsExpanded = (val) => updateCollapseState('details', val);
  const setPhotosExpanded = (val) => updateCollapseState('photos', val);
  const setDocumentsExpanded = (val) => updateCollapseState('documents', val);

  React.useEffect(() => {
    const newValue = getActualValueLocal(item);
    setEditForm({ ...item, shares: newValue, artArtist: item.artArtist || item.artist || '' });
    setPhotos(item.photos || []);
    setDocuments(item.documents || []);
    setLightboxIndex(null);
    setViewingDocument(null);
  }, [item.id]);

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < items.length - 1;

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    const newPhotos = [...photos];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'portfolio_art');
        const response = await fetch(`${API_URL}/api/upload/image`, { method: 'POST', body: formData });
        if (response.ok) {
          const data = await response.json();
          newPhotos.push(data.url);
        }
      } catch (error) { console.error('Upload error:', error); }
    }
    setPhotos(newPhotos);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDocumentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingDoc(true);
    const newDocs = [...documents];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'portfolio_art_docs');
        const response = await fetch(`${API_URL}/api/upload/image`, { method: 'POST', body: formData });
        if (response.ok) {
          const data = await response.json();
          newDocs.push({ name: file.name, url: data.url, uploadedAt: new Date().toISOString() });
        }
      } catch (error) { console.error('Document upload error:', error); }
    }
    setDocuments(newDocs);
    setUploadingDoc(false);
    if (docInputRef.current) docInputRef.current.value = '';
  };

  const handleDeletePhoto = (index) => setPhotos(photos.filter((_, i) => i !== index));
  const handleDeleteDocument = (index) => setDocuments(documents.filter((_, i) => i !== index));

  const handleSave = async (closeAfterSave = false) => {
    if (saving) return; // Prevent double-click
    setSaving(true);
    try {
      const enteredValue = parseFloat(editForm.shares) || 0;
      const updateData = { ...editForm, currentPrice: enteredValue, shares: 1, photos, documents };
      await onSave(item.id, updateData, closeAfterSave);
    } finally {
      setSaving(false);
    }
  };

  const displayName = ASSET_CONFIGS.ART?.getDisplayName?.(item) || item.symbol || item.name || 'Artwork';

  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" 
      onClick={isNewItem ? undefined : onClose}
    >
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-rose-600 to-rose-700 text-white p-4 rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!isNewItem && (
                <button onClick={() => onNavigate('prev')} disabled={!canGoPrev} className={`p-1 rounded-full ${canGoPrev ? 'hover:bg-white/20' : 'opacity-30 cursor-not-allowed'}`}>
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              <div>
                <h2 className="text-xl font-bold">{isNewItem ? 'Add New Artwork' : displayName}</h2>
                {!isNewItem && (item.artArtist || item.artist) && <p className="text-rose-100 text-sm">by {item.artArtist || item.artist}</p>}
                {!isNewItem && <p className="text-rose-200 text-xs">Item {currentIndex + 1} of {items.length}</p>}
              </div>
              {!isNewItem && (
                <button onClick={() => onNavigate('next')} disabled={!canGoNext} className={`p-1 rounded-full ${canGoNext ? 'hover:bg-white/20' : 'opacity-30 cursor-not-allowed'}`}>
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-4">
              {!isNewItem && (
                <div className="text-right">
                  <div className="text-2xl font-bold">{formatCurrency(actualValue)}</div>
                  <div className="text-rose-200 text-sm">
                    Cost: {formatCurrency(actualCost)} | {actualValue >= actualCost ? 'Appreciated' : 'Depreciated'}: {formatCurrency(Math.abs(actualValue - actualCost))}
                  </div>
                </div>
              )}
              {/* Sold/Disposed Toggle */}
              {!isNewItem && (
              <button
                type="button"
                data-testid="disposed-toggle"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditForm(prev => ({ ...prev, disposed: !prev.disposed })); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${editForm.disposed ? 'bg-red-500 text-white hover:bg-red-400' : 'bg-white/20 text-white hover:bg-white/30'}`}
                title={editForm.disposed ? 'Mark as active' : 'Mark as sold/disposed'}
              >
                <Ban className="w-4 h-4" /><span>{editForm.disposed ? 'Sold' : 'Active'}</span>
              </button>
              )}
              {/* Complete Toggle Button */}
              {!isNewItem && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditForm(prev => ({ ...prev, isComplete: !prev.isComplete }));
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    editForm.isComplete
                      ? 'bg-amber-400 text-amber-900 hover:bg-amber-300'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  title={editForm.isComplete ? 'Mark as incomplete' : 'Mark as complete'}
                >
                  <Star className={`w-4 h-4 ${editForm.isComplete ? 'fill-amber-900' : ''}`} />
                  <span>{editForm.isComplete ? 'Complete' : 'Incomplete'}</span>
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full"><X className="w-5 h-5" /></button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`p-6 space-y-6 ${editForm.disposed ? 'bg-gray-100 asset-sold' : ''}`}>
          {/* Photo Gallery */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setPhotosExpanded(!photosExpanded)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
              <span className="font-semibold text-slate-700 flex items-center gap-2">
                <Camera className="w-4 h-4" /> Photo Gallery
                {photos.length > 0 && <span className="bg-rose-100 text-rose-700 text-xs px-2 py-0.5 rounded-full">{photos.length}</span>}
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${photosExpanded ? 'rotate-180' : ''}`} />
            </button>
            {photosExpanded && (
              <div className="p-4">
                <div className="flex flex-wrap gap-3">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100 cursor-pointer border-2 border-transparent hover:border-rose-400 transition-colors" onClick={() => setLightboxIndex(idx)}>
                        <img src={photo} alt={`Artwork ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                      <button onClick={() => handleDeletePhoto(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 hover:border-rose-400 flex flex-col items-center justify-center cursor-pointer bg-slate-50 hover:bg-rose-50 transition-colors">
                    <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
                    {uploading ? <div className="animate-spin w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full" /> : <><Plus className="w-6 h-6 text-slate-400" /><span className="text-xs text-slate-400 mt-1">Add</span></>}
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Lightbox */}
          {lightboxIndex !== null && (
            <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center" onClick={() => setLightboxIndex(null)}>
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(Math.max(0, lightboxIndex - 1)); }} disabled={lightboxIndex === 0} className={`absolute left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 ${lightboxIndex === 0 ? 'opacity-30' : ''}`}>
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <img src={photos[lightboxIndex]} alt="Artwork" className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(Math.min(photos.length - 1, lightboxIndex + 1)); }} disabled={lightboxIndex === photos.length - 1} className={`absolute right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 ${lightboxIndex === photos.length - 1 ? 'opacity-30' : ''}`}>
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
              <button onClick={() => setLightboxIndex(null)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"><X className="w-6 h-6 text-white" /></button>
              <div className="absolute bottom-4 text-white text-sm">{lightboxIndex + 1} / {photos.length}</div>
            </div>
          )}

          {/* Item Details */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setDetailsExpanded(!detailsExpanded)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
              <span className="font-semibold text-slate-700 flex items-center gap-2"><Palette className="w-4 h-4" /> Artwork Details</span>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${detailsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {detailsExpanded && (
              <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Artwork Name</label>
                  <input type="text" value={editForm.symbol || ''} onChange={(e) => setEditForm({...editForm, symbol: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Artist</label>
                  <input type="text" value={editForm.artArtist || ''} onChange={(e) => setEditForm({...editForm, artArtist: e.target.value})} placeholder="Artist name" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Medium</label>
                  <select value={editForm.artMedium || ''} onChange={(e) => setEditForm({...editForm, artMedium: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500">
                    <option value="">Select Medium</option>
                    <option value="Oil Painting">Oil Painting</option>
                    <option value="Acrylic">Acrylic</option>
                    <option value="Watercolor">Watercolor</option>
                    <option value="Sculpture">Sculpture</option>
                    <option value="Photography">Photography</option>
                    <option value="Print">Print</option>
                    <option value="Mixed Media">Mixed Media</option>
                    <option value="Digital">Digital</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Year Created</label>
                  <input type="text" value={editForm.artYear || ''} onChange={(e) => setEditForm({...editForm, artYear: e.target.value})} placeholder="e.g. 1990" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Dimensions</label>
                  <input type="text" value={editForm.artDimensions || ''} onChange={(e) => setEditForm({...editForm, artDimensions: e.target.value})} placeholder='e.g. 24" x 36"' className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Edition/Serial</label>
                  <input type="text" value={editForm.serialNumber || ''} onChange={(e) => setEditForm({...editForm, serialNumber: e.target.value})} placeholder="e.g. 1/100" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Condition</label>
                  <select value={editForm.condition || ''} onChange={(e) => setEditForm({...editForm, condition: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500">
                    <option value="">Select Condition</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Very Good">Very Good</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Purchase Date</label>
                  <input type="date" value={editForm.purchaseDate || ''} onChange={(e) => setEditForm({...editForm, purchaseDate: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Where Purchased</label>
                  <input type="text" value={editForm.purchaseLocation || ''} onChange={(e) => setEditForm({...editForm, purchaseLocation: e.target.value})} placeholder="e.g. Gallery, Auction" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Storage Location</label>
                  <input type="text" value={editForm.platform || ''} onChange={(e) => setEditForm({...editForm, platform: e.target.value})} placeholder="e.g. Home, Vault" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Insured By</label>
                  <input type="text" value={editForm.insuredBy || ''} onChange={(e) => setEditForm({...editForm, insuredBy: e.target.value})} placeholder="e.g. Chubb, AXA Art" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Purchase Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input type="text" value={editForm.costBasis ? Math.round(editForm.costBasis).toLocaleString() : ''} onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setEditForm({...editForm, costBasis: parseInt(val) || 0});
                    }} className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Current Value</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input type="text" value={editForm.shares ? Math.round(editForm.shares).toLocaleString() : ''} onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setEditForm({...editForm, shares: parseInt(val) || 0});
                    }} className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                  </div>
                </div>
                <div className="col-span-2 md:col-span-3">
                  <label className="block text-xs text-slate-500 mb-1">Notes</label>
                  <textarea value={editForm.notes || ''} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} rows={2} placeholder="Provenance, exhibition history, etc." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500" />
                </div>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setDocumentsExpanded(!documentsExpanded)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
              <span className="font-semibold text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Documents
                {documents.length > 0 && <span className="bg-rose-100 text-rose-700 text-xs px-2 py-0.5 rounded-full">{documents.length}</span>}
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${documentsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {documentsExpanded && (
              <div className="p-4">
                <div className="space-y-2">
                  {documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-rose-600" />
                        <span className="text-sm text-slate-700">{doc.name}</span>
                        {doc.uploadedAt && <span className="text-xs text-slate-400">Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setViewingDocument(doc); }} className="p-1.5 hover:bg-rose-100 rounded text-rose-600" title="View"><Eye className="w-4 h-4" /></button>
                        <a href={doc.url} download={doc.name || 'document'} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-slate-200 rounded text-slate-600" title="Download"><Download className="w-4 h-4" /></a>
                        <button onClick={() => handleDeleteDocument(idx)} className="p-1.5 hover:bg-red-100 rounded text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <label className="mt-3 flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-rose-400 hover:bg-rose-50 transition-colors">
                  <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx" multiple onChange={handleDocumentUpload} className="hidden" disabled={uploadingDoc} />
                  {uploadingDoc ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 text-rose-500 animate-spin" />
                      <span className="text-sm text-rose-600">Uploading document...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-slate-400" />
                      <span className="text-sm text-slate-500">Upload Documents (Appraisals, Certificates, Provenance)</span>
                    </>
                  )}
                </label>
              </div>
            )}
          </div>

          {/* PDF Viewer - Uses iframe */}
          {viewingDocument && (
            <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setViewingDocument(null)}>
              <div className="bg-white rounded-xl w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-rose-100">
                      <FileText className="w-5 h-5 text-rose-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{viewingDocument.name}</h3>
                      <p className="text-xs text-slate-500">PDF Document</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={viewingDocument.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                      title="Open in New Tab"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                    <a
                      href={viewingDocument.url}
                      download={viewingDocument.name || 'document'}
                      className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Download"
                    >
                      <Download className="w-5 h-5" />
                    </a>
                    <button
                      onClick={() => setViewingDocument(null)}
                      className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* PDF Content - Using iframe with backend proxy for correct Content-Type */}
                <div className="flex-1 bg-slate-100 rounded-b-xl overflow-hidden relative">
                  <iframe
                    src={`${API_URL}/api/pdf-proxy?url=${encodeURIComponent(viewingDocument.url)}`}
                    className="w-full h-full border-0"
                    title={viewingDocument.name}
                    onError={() => console.log('PDF iframe error')}
                  />
                  {/* Fallback link shown at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 bg-slate-800/90 text-white text-xs p-2">
                    <div className="flex items-center justify-between">
                      <span>If PDF doesn&apos;t load:</span>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => navigator.clipboard.writeText(viewingDocument.url)}
                          className="text-slate-400 hover:text-white"
                          title="Copy URL"
                        >
                          Copy URL
                        </button>
                        <a 
                          href={viewingDocument.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-rose-300 hover:text-rose-200 underline"
                        >
                          Open directly
                        </a>
                      </div>
                    </div>
                    <div className="mt-1 text-slate-400 truncate text-[10px]" title={viewingDocument.url}>
                      {viewingDocument.url}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-3 rounded-b-xl">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => handleSave(false)} disabled={saving || uploadingDoc} className="bg-rose-500 hover:bg-rose-600 text-white disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving || uploadingDoc} className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            {saving ? 'Saving...' : 'Save & Close'}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default ArtDetailModal;
